import {
	Scene,
	Vector3,
	MeshBuilder,
	StandardMaterial,
	PBRMaterial,
	Color3,
	TransformNode,
	Mesh,
} from "@babylonjs/core";
import { AdvancedDynamicTexture, Rectangle, StackPanel, TextBlock, Control } from "@babylonjs/gui";
import { buildStorageRack, buildPackingStation, type StorageRack } from "./racks";

export interface PrinterLabel {
	root: Rectangle;
	title: TextBlock;
	status: TextBlock;
	progress: TextBlock;
}

export interface PrinterMesh {
	id: string;
	root: Mesh;
	bed: Mesh;
	print: Mesh;
	gantry: Mesh;
	hotend: Mesh;
	spool: Mesh;
	label: PrinterLabel;
}

export interface WarehouseHandles {
	root: TransformNode;
	printers: Map<string, PrinterMesh>;
	addPrinter: (id: string) => PrinterMesh;
	removePrinter: (id: string) => void;
	rack: StorageRack;
	packing: { addBox: () => void; reset: () => void };
	/** Adjust the number of visible printer rack frames (1..4). */
	setRackCount: (n: number) => void;
}

// Each rack holds 4 printer slots in a row. Racks stack along Z inside the
// warehouse. Up to 4 racks fit (the warehouse is 18 deep).
const RACK_Z_POSITIONS = [-1, 2, 5, 8];
const SLOT_X_POSITIONS = [-3, -1, 1, 3];
const SHELF_Y = 0.9;
const MAX_RACKS = RACK_Z_POSITIONS.length;

function computeSlotPositions(rackCount: number): Vector3[] {
	const slots: Vector3[] = [];
	const racks = Math.min(MAX_RACKS, Math.max(0, rackCount));
	for (let r = 0; r < racks; r++) {
		const z = RACK_Z_POSITIONS[r]!;
		for (const x of SLOT_X_POSITIONS) {
			slots.push(new Vector3(x, SHELF_Y, z));
		}
	}
	return slots;
}

function buildPrinterRack(scene: Scene, parent: TransformNode, z: number, frameMat: PBRMaterial, shelfMat: PBRMaterial) {
	// Steel rack frame: 4 vertical posts + 1 horizontal shelf.
	const W = 9; // along X
	const D = 1.2; // along Z
	const H_SHELF = 0.85; // shelf top
	const POST_T = 0.1;

	for (const x of [-4.4, -1.4, 1.6, 4.4])
		for (const dz of [-D / 2, D / 2]) {
			const post = MeshBuilder.CreateBox(`rack-post-${z}-${x}-${dz}`, { width: POST_T, depth: POST_T, height: 2.2 }, scene);
			post.position.set(x, 1.1, z + dz);
			post.material = frameMat;
			post.parent = parent;
		}
	// Wood-toned shelf (looks like a workshop pegboard surface)
	const shelf = MeshBuilder.CreateBox(`rack-shelf-${z}`, { width: W, depth: D, height: 0.08 }, scene);
	shelf.position.set(0, H_SHELF, z);
	shelf.material = shelfMat;
	shelf.parent = parent;
	// Top crossbar — same metal as posts
	const top = MeshBuilder.CreateBox(`rack-top-${z}`, { width: W, depth: D, height: 0.06 }, scene);
	top.position.set(0, 2.18, z);
	top.material = frameMat;
	top.parent = parent;
	// Diagonal cross-brace on the back to read as a real industrial rack
	const brace = MeshBuilder.CreateBox(`rack-brace-${z}`, { width: 0.04, depth: 0.04, height: W * 1.05 }, scene);
	brace.position.set(0, 1.5, z + D / 2 - 0.05);
	brace.rotation.z = Math.PI / 2;
	brace.rotation.y = 0.06;
	brace.material = frameMat;
	brace.parent = parent;
}

export function buildWarehouse(scene: Scene, origin = new Vector3(0, 0, 8)): WarehouseHandles {
	const root = new TransformNode("warehouse", scene);
	root.position.copyFrom(origin);

	// One fullscreen ADT for all 3D-anchored labels in the warehouse.
	const adt = AdvancedDynamicTexture.CreateFullscreenUI("printer-labels", true, scene);

	// Big "YOUR WORKSHOP" sign above the south doorway so players can find it.
	const signAnchor = MeshBuilder.CreateBox("workshop-sign-anchor", { size: 0.1 }, scene);
	signAnchor.isVisible = false;
	signAnchor.parent = root;
	signAnchor.position.set(0, 6, -9);
	const signRect = new Rectangle("workshop-sign");
	signRect.width = "260px";
	signRect.height = "50px";
	signRect.cornerRadius = 10;
	signRect.thickness = 3;
	signRect.color = "#fc7";
	signRect.background = "rgba(40,20,0,0.85)";
	adt.addControl(signRect);
	signRect.linkWithMesh(signAnchor as unknown as Mesh);
	signRect.linkOffsetY = -20;
	const signText = new TextBlock();
	signText.text = "🛠️ YOUR WORKSHOP";
	signText.color = "#ffe0b0";
	signText.fontSize = 20;
	signText.fontWeight = "bold";
	signText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
	signRect.addControl(signText);

	const W = 12;
	const D = 18;
	const H = 5;
	const WALL_T = 0.25;
	const DOOR_W = 3;

	const concrete = new StandardMaterial("concrete", scene);
	concrete.diffuseColor = new Color3(0.55, 0.55, 0.55);
	const wallMat = new StandardMaterial("wall", scene);
	wallMat.diffuseColor = new Color3(0.78, 0.55, 0.25);
	const roofMat = new StandardMaterial("roof", scene);
	roofMat.diffuseColor = new Color3(0.18, 0.18, 0.2);

	const floor = MeshBuilder.CreateBox("floor", { width: W, depth: D, height: 0.1 }, scene);
	floor.position.y = 0.05;
	floor.material = concrete;
	floor.parent = root;

	const roof = MeshBuilder.CreateBox("roof", { width: W + 0.4, depth: D + 0.4, height: 0.2 }, scene);
	roof.position.y = H + 0.1;
	roof.material = roofMat;
	roof.parent = root;

	const mkWall = (name: string, w: number, d: number, x: number, z: number) => {
		const wall = MeshBuilder.CreateBox(name, { width: w, depth: d, height: H }, scene);
		wall.position.set(x, H / 2, z);
		wall.material = wallMat;
		wall.parent = root;
		wall.checkCollisions = true;
		return wall;
	};

	mkWall("wall-n", W, WALL_T, 0, D / 2);
	mkWall("wall-e", WALL_T, D, W / 2, 0);
	mkWall("wall-w", WALL_T, D, -W / 2, 0);
	const southSegW = (W - DOOR_W) / 2;
	mkWall("wall-s-left", southSegW, WALL_T, -(DOOR_W / 2 + southSegW / 2), -D / 2);
	mkWall("wall-s-right", southSegW, WALL_T, DOOR_W / 2 + southSegW / 2, -D / 2);

	// Industrial printer racks — printer meshes sit on their shelves. Count
	// scales with player.printerRacks; default 2 to match the starter state.
	// PBR materials for a real "brushed aluminum + wood pegboard" look.
	const rackFrameMat = new PBRMaterial("rack-frame-mat", scene);
	rackFrameMat.albedoColor = new Color3(0.5, 0.55, 0.62);
	rackFrameMat.metallic = 0.85;
	rackFrameMat.roughness = 0.35;
	const shelfMat = new PBRMaterial("rack-shelf-mat", scene);
	shelfMat.albedoColor = new Color3(0.55, 0.36, 0.18); // walnut wood
	shelfMat.metallic = 0;
	shelfMat.roughness = 0.7;
	let rackCount = 2;
	let rackFrames: TransformNode[] = [];
	let slotPositions: Vector3[] = computeSlotPositions(rackCount);
	const printers = new Map<string, PrinterMesh>();

	const rebuildRacks = (n: number) => {
		const clamped = Math.max(1, Math.min(MAX_RACKS, Math.floor(n)));
		if (clamped === rackCount && rackFrames.length > 0) return;
		// Dispose existing frames.
		for (const f of rackFrames) f.dispose();
		rackFrames = [];
		for (let r = 0; r < clamped; r++) {
			const frame = new TransformNode(`rack-frame-${r}`, scene);
			frame.parent = root;
			buildPrinterRack(scene, frame, RACK_Z_POSITIONS[r]!, rackFrameMat, shelfMat);
			rackFrames.push(frame);
		}
		rackCount = clamped;
		slotPositions = computeSlotPositions(rackCount);
		// Re-seat any existing printer meshes onto the new slot grid.
		let i = 0;
		for (const mesh of printers.values()) {
			if (i < slotPositions.length) mesh.root.position.copyFrom(slotPositions[i]!);
			i++;
		}
	};
	rebuildRacks(rackCount);

	const addPrinter = (id: string): PrinterMesh => {
		const idx = printers.size % slotPositions.length;
		const mesh = buildPrinter(scene, id, adt);
		mesh.root.parent = root;
		mesh.root.position.copyFrom(slotPositions[idx]!);
		printers.set(id, mesh);
		return mesh;
	};

	const removePrinter = (id: string) => {
		const p = printers.get(id);
		if (!p) return;
		p.label.root.dispose();
		p.root.dispose();
		printers.delete(id);
	};

	// Storage rack against the east wall (rotated to face into the room),
	// packing station near the south doorway. Both live in warehouse-local
	// space so they move with the warehouse if we ever re-anchor it.
	const rack = buildStorageRack(scene, new Vector3(W / 2 - 0.8, 0, 2));
	rack.root.parent = root;
	rack.root.rotation.y = -Math.PI / 2;
	rack.setInventory({});

	const packing = buildPackingStation(scene, new Vector3(3, 0, -D / 2 + 2));
	// Re-parent the packing TransformNode under the warehouse root so it inherits position.
	const packingRoot = scene.getTransformNodeByName("packing");
	if (packingRoot) {
		packingRoot.parent = root;
		// buildPackingStation set absolute position to origin; localPosition is now origin.
		packingRoot.position.set(3, 0, -D / 2 + 2);
	}

	return { root, printers, addPrinter, removePrinter, rack, packing, setRackCount: rebuildRacks };
}

function buildPrinter(scene: Scene, id: string, adt: AdvancedDynamicTexture): PrinterMesh {
	// PBR materials — matte black industrial plastic, glass-y bed, brass nozzle,
	// brushed-aluminum rails. Reads as actual hardware instead of cubes.
	const frameMat = new PBRMaterial(`printer-frame-${id}`, scene);
	frameMat.albedoColor = new Color3(0.06, 0.06, 0.08);
	frameMat.metallic = 0.05;
	frameMat.roughness = 0.55;
	const bedMat = new PBRMaterial(`printer-bed-mat-${id}`, scene);
	bedMat.albedoColor = new Color3(0.16, 0.16, 0.18);
	bedMat.metallic = 0.2;
	bedMat.roughness = 0.25; // shinier glass-like print surface
	const nozzleMat = new PBRMaterial(`printer-nozzle-${id}`, scene);
	nozzleMat.albedoColor = new Color3(0.85, 0.65, 0.2);
	nozzleMat.metallic = 0.95;
	nozzleMat.roughness = 0.3; // polished brass
	const railMat = new PBRMaterial(`printer-rail-${id}`, scene);
	railMat.albedoColor = new Color3(0.7, 0.72, 0.78);
	railMat.metallic = 0.9;
	railMat.roughness = 0.4; // brushed aluminum extrusion

	const base = MeshBuilder.CreateBox(`printer-${id}`, { width: 0.6, depth: 0.6, height: 0.1 }, scene);
	base.position.y = 0.05;
	base.material = frameMat;
	base.metadata = { printerId: id };

	const mkChild = (name: string, opts: { w: number; d: number; h: number }, pos: Vector3, mat: PBRMaterial | StandardMaterial) => {
		const m = MeshBuilder.CreateBox(name, { width: opts.w, depth: opts.d, height: opts.h }, scene);
		m.position.copyFrom(pos);
		m.material = mat;
		m.parent = base;
		return m;
	};

	const post = (x: number, z: number) =>
		mkChild(`post-${id}-${x}-${z}`, { w: 0.05, d: 0.05, h: 0.9 }, new Vector3(x, 0.45, z), frameMat);
	post(0.27, 0.27);
	post(-0.27, 0.27);
	post(0.27, -0.27);
	post(-0.27, -0.27);

	const bed = mkChild(`printer-bed-${id}`, { w: 0.5, d: 0.5, h: 0.02 }, new Vector3(0, 0.12, 0), bedMat);

	// Print material — bright extruded PLA. Slight emissive sells the "warm".
	const printMat = new PBRMaterial(`printer-print-${id}`, scene);
	printMat.albedoColor = new Color3(0.95, 0.55, 0.2);
	printMat.emissiveColor = new Color3(0.18, 0.06, 0.0);
	printMat.metallic = 0;
	printMat.roughness = 0.35;
	// Use a cylinder so it looks more like an extrusion than a square block.
	const print = MeshBuilder.CreateCylinder(`printer-print-${id}`, {
		diameter: 0.3,
		height: 1,
		tessellation: 16,
	}, scene);
	print.material = printMat;
	print.parent = bed;
	print.scaling.y = 0.001;
	print.position.y = 0.51;
	print.isVisible = false;

	// X-axis rail above bed.
	mkChild(`rail-x-${id}`, { w: 0.6, d: 0.04, h: 0.04 }, new Vector3(0, 0.9, 0.27), railMat);
	mkChild(`rail-x2-${id}`, { w: 0.6, d: 0.04, h: 0.04 }, new Vector3(0, 0.9, -0.27), railMat);

	// Gantry slides on X. We'll animate this transform's x in the render loop.
	const gantry = mkChild(`gantry-${id}`, { w: 0.08, d: 0.6, h: 0.04 }, new Vector3(0, 0.9, 0), railMat);

	// Hotend slides on Z along the gantry. Parented to gantry so X moves with it.
	const hotend = MeshBuilder.CreateBox(`hotend-${id}`, { width: 0.07, depth: 0.07, height: 0.14 }, scene);
	hotend.position.set(0, -0.09, 0);
	hotend.material = nozzleMat;
	hotend.parent = gantry;

	// Filament spool — pivot is tilted so the spool lies horizontally; the
	// cylinder itself spins around its own local Y (its symmetry axis), so
	// we use `rotate(Up, ..., LOCAL)` later instead of `rotation.y +=` to
	// avoid spinning around world Y (which would orbit instead of spin).
	// Filament spool — glossy plastic with vivid color
	const spoolMat = new PBRMaterial(`spool-${id}`, scene);
	spoolMat.albedoColor = new Color3(0.95, 0.55, 0.2);
	spoolMat.metallic = 0;
	spoolMat.roughness = 0.25; // shiny PLA
	const spoolPivot = new TransformNode(`spool-pivot-${id}`, scene);
	spoolPivot.parent = base;
	spoolPivot.position.set(0.45, 0.7, 0);
	spoolPivot.rotation.z = Math.PI / 2;
	const spool = MeshBuilder.CreateCylinder(`spool-${id}`, {
		diameter: 0.35,
		height: 0.08,
		tessellation: 16,
	}, scene);
	spool.material = spoolMat;
	spool.parent = spoolPivot;
	// Hub at center to make the rotation visible.
	const hub = MeshBuilder.CreateCylinder(`hub-${id}`, {
		diameter: 0.06,
		height: 0.1,
		tessellation: 12,
	}, scene);
	hub.material = frameMat;
	hub.parent = spool;

	const labelAnchor = new TransformNode(`label-anchor-${id}`, scene);
	labelAnchor.parent = base;
	labelAnchor.position.set(0, 1.2, 0);

	const label = new Rectangle(`label-bg-${id}`);
	label.width = "240px";
	label.height = "72px";
	label.cornerRadius = 6;
	label.color = "#7df";
	label.thickness = 1;
	label.background = "rgba(0,0,0,0.65)";
	adt.addControl(label);
	label.linkWithMesh(labelAnchor as unknown as Mesh);
	label.linkOffsetY = -10;

	const stack = new StackPanel();
	stack.isVertical = true;
	stack.height = "72px";
	stack.width = "230px";
	label.addControl(stack);

	const title = new TextBlock(`label-title-${id}`);
	title.text = "printer";
	title.color = "#fff";
	title.fontSize = 13;
	title.fontWeight = "bold";
	title.height = "20px";
	title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
	stack.addControl(title);

	const status = new TextBlock(`label-status-${id}`);
	status.text = "idle";
	status.color = "#9c9";
	status.fontSize = 12;
	status.height = "20px";
	status.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
	stack.addControl(status);

	const progress = new TextBlock(`label-progress-${id}`);
	progress.text = "";
	progress.color = "#fd6";
	progress.fontSize = 11;
	progress.height = "28px"; // taller so the wrapped line fits
	progress.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
	progress.textWrapping = true; // allow wrap so long client names aren't truncated
	stack.addControl(progress);

	return { id, root: base, bed, print, gantry, hotend, spool, label: { root: label, title, status, progress } };
}
